import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import { ColumnContent, Group, Header, PersonalText, Container } from '../../styles';
import {
  Autotext,
  BottomContent,
  Box,
  Content,
  Description,
  Icon,
  Label,
  LeftContent,
  RightContent,
  Text,
  TitleSection
} from './styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import { updateAppsSettings } from '../../../../services/settingServices';
import UserPreferenceSingleton from '../../../../helpers/userPreferenceSingleton';
import { updateCurrentWorkspaceLocally } from '../../../../services/workspaceService';
import { isEmpty } from '../../../../helpers/common';
import { captureException } from '../../../../services/logService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import SwitchInput from '../../../../component/input/switchInput';
import { ICON_SIZE } from '../../../../global/constants';

interface AppItem {
  comingSoon?: boolean;
  checked: boolean;
  propsName: string;
  title?: string;
  icon?: string;
  description?: string;
}

const Apps: React.FC = () => {
  //Mobx state variables
  const { workspaceStore } = rootStore;
  const { currentWorkspace: workspace } = workspaceStore;
  const workspace_id = workspace?.id || '';

  const [appSettings, setAppSettings] = useState({
    invoice: true,
    customer: true,
    timetracking: true,
    messages: true,
    views: false
  });

  // Other variables
  const isMobile = useMobileDevice();

  // Loads and initializes app settings data from workspace
  const loadData = useCallback(async () => {
    try {
      if (!isEmpty(workspace?.id)) {
        const apps = workspace?.apps;
        setAppSettings({
          invoice: typeof apps?.invoiceAndPayment === 'boolean' ? apps?.invoiceAndPayment : true,
          customer: typeof apps?.customer === 'boolean' ? apps?.customer : true,
          timetracking: typeof apps?.timeTracking === 'boolean' ? apps?.timeTracking : true,
          messages: typeof apps?.messages === 'boolean' ? apps?.messages : true,
          views: apps?.views || false
        });
      }
    } catch (error) {
      captureException(error);
    }
  }, [workspace?.apps, workspace?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handles changes to app settings and updates them in the backend
  const onChangeAppSettings = useCallback(
    async (propsName: string, value: boolean) => {
      try {
        const userDetails = await UserPreferenceSingleton.getInstance().getCurrentUser();
        setAppSettings((prev) => ({
          ...prev,
          [propsName]: value
        }));

        const payload = {
          userId: userDetails?.id,
          companyId: workspace_id,
          invoicePayment: appSettings?.invoice,
          customer: appSettings?.customer,
          timeTracking: appSettings?.timetracking,
          messages: appSettings?.messages,
          views: appSettings?.views
        };

        if (propsName === 'invoice') {
          payload.invoicePayment = value;
        } else if (propsName === 'customer') {
          payload.customer = value;
        } else if (propsName === 'timetracking') {
          payload.timeTracking = value;
        } else if (propsName === 'messages') {
          payload.messages = value;
        } else if (propsName === 'views') {
          payload.views = value;
        }

        const response = await updateAppsSettings(payload);
        if (response && workspace?.apps) {
          updateCurrentWorkspaceLocally({
            apps: {
              calendar: workspace.apps.calendar ?? false,
              invoiceAndPayment: payload.invoicePayment,
              customer: payload.customer,
              timeTracking: payload.timeTracking,
              messages: payload.messages,
              views: payload.views
            }
          });
        }
      } catch (error) {
        captureException(error);
      }
    },
    [appSettings, workspace?.apps, workspace_id]
  );

  // Array containing configuration data for all available apps
  const appData = [
    {
      icon: 'invoice-icon',
      title: 'Invoice & Payment',
      description: 'Invoicing and payments made easier by Teamcamp. Now money management is easier than ever.',
      propsName: 'invoice',
      checked: appSettings?.invoice
    },
    {
      icon: 'customer-icon',
      title: 'Customer',
      description: 'Group projects by client and invite them for collaboration.',
      propsName: 'customer',
      checked: appSettings?.customer
    },
    {
      icon: 'time-icon',
      title: 'Time Tracking',
      description:
        'This feature keeps an accurate track of all your work. Make billing your clients easy and hassle free.',
      propsName: 'timetracking',
      checked: appSettings?.timetracking
    },
    {
      icon: 'message-icon',
      title: 'Messages',
      description:
        'Elevate communication with integrated messaging for seamless collaboration within the teams and projects.',
      propsName: 'messages',
      checked: appSettings?.messages
    },
    {
      icon: 'navbar-views-icon',
      title: 'Views',
      description: 'Simplify your task list by creating views that show only what you need.',
      propsName: 'views',
      checked: appSettings?.views
    }
  ];

  const renderRightSection = (app: AppItem) => {
    return app.comingSoon ? (
      <Label>
        <p>Coming soon</p>
      </Label>
    ) : (
      <RightContent>
        <Autotext>
          <SwitchInput checked={app.checked} onChange={() => onChangeAppSettings(app.propsName, !app.checked)} />
        </Autotext>
      </RightContent>
    );
  };

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <Header>
                <PersonalText>Apps</PersonalText>
              </Header>
            ) : (
              <ResponsiveHeader title='Apps' />
            )}
            <Content>
              <ColumnContent>
                <BottomContent>
                  {appData.map((app) => (
                    <Box key={app?.title}>
                      <LeftContent>
                        <Icon>
                          <SVGIcon
                            name={app.icon}
                            width={ICON_SIZE.XXL}
                            height={ICON_SIZE.XXL}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-primary)'
                          />
                        </Icon>
                        <Text>
                          <TitleSection>
                            <h1>{app.title}</h1>
                            {isMobile && renderRightSection(app)}
                          </TitleSection>
                          <Description>
                            <p>{app.description}</p>
                          </Description>
                        </Text>
                      </LeftContent>
                      {!isMobile && renderRightSection(app)}
                    </Box>
                  ))}
                </BottomContent>
              </ColumnContent>
            </Content>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Apps);
